<template>
  <div class="login">
    <div class="login__card">
      <div class="login__brand">
        <img class="login__logo" :src="$brand.logo" :alt="$brand.name" />
        <div>
          <h1 class="login__brand-name">{{ $brand.name }}</h1>
          <p class="login__brand-tag">{{ $t('shell.workspace') }}</p>
        </div>
      </div>

      <h2 class="login__title">{{ $t('login-view.title') }}</h2>

      <ValidationObserver v-slot="{ invalid, handleSubmit }" ref="observer">
        <form @submit.prevent="handleSubmit(login)" novalidate>
          <OmxField
              v-model="email"
              type="email"
              :label="$t('login-view.email-placeholder')"
              :placeholder="$t('login-view.email-placeholder')"
              icon="email-outline"
              rules="required|email"
              required
              autocomplete="email"
              tabindex="1"
              @enter="handleSubmit(login)"
          />
          <OmxField
              v-model="password"
              type="password"
              :label="$t('login-view.password-placeholder')"
              :placeholder="$t('login-view.password-placeholder')"
              icon="lock-outline"
              rules="required"
              required
              autocomplete="current-password"
              tabindex="2"
              @enter="handleSubmit(login)"
          />

          <div class="login__row">
            <router-link to="/reset-password" tabindex="3" class="login__link">
              {{ $t('login-view.password-reset-button') }}
            </router-link>
          </div>

          <b-button
              native-type="submit"
              type="is-primary"
              :loading="isLoading"
              :disabled="invalid || isLoading"
              expanded
              size="is-medium"
              tabindex="4"
          >{{ $t('login-view.login-button') }}</b-button>

          <p class="login__signup">
            {{ $t('login-view.create-account-text-part1') }}
            <router-link to="/sign-up" tabindex="5">{{ $t('login-view.create-account-text-part2') }}</router-link>
          </p>
        </form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import firebase from "firebase/app";
import { ValidationObserver } from "vee-validate";
import OmxField from "@/components/OmxField.vue";

export default {
  name: "login",
  components: { ValidationObserver, OmxField },
  data() {
    return {
      email: "",
      password: "",
      isLoading: false
    };
  },
  computed: {
    isEmailVerified() {
      const u = firebase.auth().currentUser;
      return !!u && u.emailVerified;
    }
  },
  methods: {
    login() {
      this.isLoading = true;
      firebase.auth()
          .signInWithEmailAndPassword(this.email, this.password)
          .then(() => {
            if (this.isEmailVerified) {
              this.checkUserRoleAndContinue();
            } else {
              this.resendVerification();
            }
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(error.message);
          });
    },
    checkUserRoleAndContinue() {
      this.loadUserRoleFromServer().then((role) => {
        this.isLoading = false;
        if (role === undefined || role === '' || role === "inactive") {
          this.$omxAlert({
            title: this.$t('pending-user.title').toString(),
            message: this.$t('pending-user.description').toString(),
            type: 'is-info',
            confirmText: this.$t('common.ok').toString()
          });
          firebase.auth().signOut();
        } else {
          this.$router.replace("projects").catch(() => { /* navigation duplicated */ });
        }
      });
    },
    resendVerification() {
      const currentUser = firebase.auth().currentUser;
      if (!currentUser) {
        this.isLoading = false;
        return;
      }
      currentUser.sendEmailVerification()
          .then(() => {
            this.isLoading = false;
            this.showSuccess(this.$t("user-verification.verification-email-sent"));
            this.$omxAlert({
              title: this.$t('user-verification.title').toString(),
              message: this.$t('user-verification.description').toString() + " " + this.email,
              type: 'is-info',
              confirmText: this.$t('common.ok').toString()
            });
            firebase.auth().signOut();
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(error.message);
          });
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.login {
  min-height: 100vh;
  background:
      radial-gradient(circle at 90% 0%, rgba(255, 175, 55, .18), transparent 45%),
      radial-gradient(circle at 0% 100%, rgba(54, 146, 219, .14), transparent 50%),
      $omx-bg;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
}

.login__card {
  width: 100%;
  max-width: 420px;
  background: $omx-surface;
  border-radius: $omx-radius-xl;
  box-shadow: $omx-shadow-lg;
  border: 1px solid $omx-ink-100;
  padding: 32px;
}

.login__brand {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 22px;
}
.login__logo {
  width: 38px; height: 38px;
  border-radius: 10px;
  object-fit: contain;
  background: #fff;
  padding: 3px;
}
.login__brand-name {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -.01em;
  color: $omx-ink-900;
}
.login__brand-tag {
  margin: 0;
  font-size: 12px;
  color: $omx-ink-500;
}

.login__title {
  margin: 0 0 22px;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -.012em;
  color: $omx-ink-900;
}

.login__row {
  display: flex;
  justify-content: flex-end;
  margin: 4px 0 18px;
  font-size: 12.5px;
}
.login__link {
  color: $omx-brand-600;
  font-weight: 600;
  &:hover { color: $omx-brand-700; }
}

.login__signup {
  margin: 18px 0 0;
  font-size: 13px;
  color: $omx-ink-500;
  text-align: center;
  a {
    color: $omx-brand-600;
    font-weight: 600;
    &:hover { color: $omx-brand-700; }
  }
}
</style>
